import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog/Dialog';
import DialogTitle from '@mui/material/DialogTitle/DialogTitle';
import DialogContent from '@mui/material/DialogContent/DialogContent';
import DialogActions from '@mui/material/DialogActions/DialogActions';
import Stack from '@mui/material/Stack/Stack';
import TextField from '@mui/material/TextField/TextField';
import Autocomplete from '@mui/material/Autocomplete/Autocomplete';
import Button from '@mui/material/Button/Button';
import Chip from '@mui/material/Chip/Chip';
import Typography from '@mui/material/Typography/Typography';
import Alert from '@mui/material/Alert/Alert';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import Checkbox from '@mui/material/Checkbox/Checkbox';
import Switch from '@mui/material/Switch/Switch';
import InputAdornment from '@mui/material/InputAdornment/InputAdornment';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Editor from '../Editor/Editor';
import { HackathonPayload } from '../../services/hackathon.service';
import { createSession, SessionPayload, slugify, updateSession } from '../../services/session.service';
import { IdeaPayload } from '../../services/idea.service';

interface Person {
    _id: string;
    username: string;
    email: string;
}

const personLabel = (p: Person) => p.username || p.email;

const FollowSwitch = ({ checked, onChange, label }: { checked: boolean, onChange: (checked: boolean) => void, label: string }) => (
    <FormControlLabel
        control={<Switch size="small" checked={checked} onChange={e => onChange(e.target.checked)} />}
        label={<Typography variant="body2">{label}</Typography>} />
)

/**
 * Create or edit a session. Organizers only - hosts edit descriptions from the session page.
 */
export const SessionDialog = ({
    hackathonId,
    hackathon,
    session,
    open,
    onClose,
    onSaved
}: {
    // as in the URL (slug or id); sessions are cached under this key
    hackathonId: string,
    hackathon: HackathonPayload,
    // omitted to create a new session
    session?: SessionPayload,
    open: boolean,
    onClose: () => void,
    onSaved: (session: SessionPayload, notified: number) => void
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [name, setName] = useState(session?.name || '');
    const [slug, setSlug] = useState(session?.slug || '');
    const [slugEdited, setSlugEdited] = useState(!!session);
    const [description, setDescription] = useState(session?.description || '');
    const [categories, setCategories] = useState<string[]>(session?.categories || []);
    const [judges, setJudges] = useState<Person[]>(session?.judges || []);
    const [hosts, setHosts] = useState<Person[]>(session?.hosts || []);
    const [ideaIds, setIdeaIds] = useState<string[]>(session?.ideas || []);
    const [allCategories, setAllCategories] = useState(!!session?.allCategories);
    const [allIdeas, setAllIdeas] = useState(!!session?.allIdeas);
    const [allJudges, setAllJudges] = useState(!!session?.allJudges);
    const [notify, setNotify] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const judgeOptions: Person[] = useMemo(() => hackathon.judges.map(j => ({ _id: j._id, username: j.username, email: j.email })), [hackathon.judges]);
    const hostOptions: Person[] = useMemo(() => {
        const people = new Map<string, Person>();
        [...hackathon.organizers, ...hackathon.judges, ...hackathon.participants].forEach(p => {
            if (p?._id && !people.has(p._id)) {
                people.set(p._id, { _id: p._id, username: p.username, email: p.email });
            }
        });
        return Array.from(people.values()).sort((a, b) => personLabel(a).localeCompare(personLabel(b)));
    }, [hackathon]);
    const ideaOptions = useMemo(() => hackathon.ideas.filter(i => !i.archived), [hackathon.ideas]);
    const selectedIdeas = ideaIds.map(id => ideaOptions.find(i => i._id === id)).filter(Boolean) as IdeaPayload[];

    const descriptionChanged = !!session && description !== session.description;
    const effectiveSlug = slugEdited ? slug : slugify(name);

    const save = () => {
        if (!name.trim()) {
            setError('Give the session a name');
            return;
        }
        setSaving(true);
        setError('');
        const changes = {
            name,
            // an untouched URL is generated (and de-duplicated) by the server
            slug: slugEdited ? slug : undefined,
            description,
            categories,
            judges: judges.map(j => j._id),
            hosts: hosts.map(h => h._id),
            ideas: ideaIds,
            allCategories,
            allIdeas,
            allJudges
        };
        const request = session
            ? updateSession(hackathonId, session._id, changes, notify && descriptionChanged)
            : createSession(hackathonId, changes).then(created => ({ session: created, notified: 0 }));
        request.then(({ session: saved, notified }) => {
            onSaved(saved, notified);
        }, err => {
            setError(err.response?.data?.message || 'Could not save the session');
        }).finally(() => setSaving(false));
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
            <DialogTitle>{session ? `Edit ${session.name}` : 'New session'}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Name"
                            required
                            fullWidth
                            autoFocus={!session}
                            value={name}
                            placeholder="Room A"
                            onChange={e => setName(e.target.value)} />
                        <TextField
                            label="URL"
                            fullWidth
                            value={effectiveSlug}
                            helperText={session && slug !== session.slug ? 'Changing this breaks links people already have' : 'Lowercase letters, numbers and dashes'}
                            onChange={e => {
                                setSlugEdited(true);
                                setSlug(e.target.value.toLowerCase());
                            }}
                            InputProps={{ startAdornment: <InputAdornment position="start">/s/</InputAdornment> }} />
                    </Stack>

                    <Stack spacing={1}>
                        <Typography variant="subtitle2">Description</Typography>
                        <Typography variant="body2" color="text.secondary">Where to go: meeting link, room location, anything people need on the day.</Typography>
                        <React.Suspense fallback={<div>Loading...</div>}>
                            <Editor toolbar={true} value={description} onChange={setDescription} />
                        </React.Suspense>
                        {
                            descriptionChanged && (
                                <FormControlLabel
                                    control={<Checkbox checked={notify} onChange={e => setNotify(e.target.checked)} />}
                                    label="Notify everyone in this session about the new description" />
                            )
                        }
                    </Stack>

                    <Stack spacing={1}>
                        <Typography variant="subtitle2">Categories judged here</Typography>
                        <FollowSwitch checked={allCategories} onChange={setAllCategories} label="All categories, including ones added later" />
                        {
                            allCategories ? null : hackathon.categories.length === 0
                                ? <Typography variant="body2" color="text.secondary">This hackathon has no categories yet. Add them in the hackathon settings.</Typography>
                                : (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {
                                            hackathon.categories.map((label, index) => {
                                                const id = hackathon.categoryMeta[index]?._id;
                                                if (!id) {
                                                    return null;
                                                }
                                                const selected = categories.includes(id);
                                                return (
                                                    <Chip
                                                        key={id}
                                                        label={label}
                                                        color={selected ? 'primary' : 'default'}
                                                        variant={selected ? 'filled' : 'outlined'}
                                                        aria-pressed={selected}
                                                        onClick={() => setCategories(selected ? categories.filter(c => c !== id) : [...categories, id])} />
                                                );
                                            })
                                        }
                                    </Stack>
                                )
                        }
                    </Stack>

                    <Typography variant="subtitle2" sx={{ mb: '-16px !important' }}>Teams</Typography>
                    <FollowSwitch checked={allIdeas} onChange={setAllIdeas} label="All teams, including ones added later" />
                    <Autocomplete
                        multiple
                        disabled={allIdeas}
                        options={ideaOptions}
                        value={selectedIdeas}
                        getOptionLabel={i => i.title}
                        isOptionEqualToValue={(a, b) => a._id === b._id}
                        onChange={(_, value) => setIdeaIds(value.map(i => i._id))}
                        renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                                <Stack>
                                    <span>{option.title}</span>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.members.map(m => m.username).join(', ') || 'No team yet'}
                                    </Typography>
                                </Stack>
                            </li>
                        )}
                        renderInput={params => <TextField {...params} label="Teams presenting" helperText={allIdeas
                            ? 'Every team presents here. Hosts set the order on the session page.'
                            : 'In presenting order. A team can be in more than one session.'} />} />

                    <Typography variant="subtitle2" sx={{ mb: '-16px !important' }}>Judges</Typography>
                    <FollowSwitch checked={allJudges} onChange={setAllJudges} label="All of the hackathon's judges, including ones added later" />
                    <Autocomplete
                        multiple
                        disabled={allJudges}
                        options={judgeOptions}
                        value={judges}
                        getOptionLabel={personLabel}
                        isOptionEqualToValue={(a, b) => a._id === b._id}
                        onChange={(_, value) => setJudges(value)}
                        noOptionsText="Add judges to the hackathon first (Organize page)"
                        renderInput={params => <TextField {...params} label="Judges" />} />

                    <Autocomplete
                        multiple
                        options={hostOptions}
                        value={hosts}
                        getOptionLabel={personLabel}
                        isOptionEqualToValue={(a, b) => a._id === b._id}
                        onChange={(_, value) => setHosts(value)}
                        renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                                <Stack>
                                    <span>{personLabel(option)}</span>
                                    <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                                </Stack>
                            </li>
                        )}
                        renderInput={params => <TextField {...params} label="Hosts" helperText="Hosts run this room on the day and can update its description" />} />

                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={saving} onClick={save}>{session ? 'Save' : 'Create session'}</Button>
            </DialogActions>
        </Dialog>
    )
}
